import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useAnchoredPanel from '../common/useAnchoredPanel';
import type { ReactNode } from 'react';

/**
 * The phase Run button, with its variants folded into a caret menu — the same
 * split-button shape the boardroom's Run uses (boardroom/RunControl).
 *
 * Two things used to float loose on the phase panels and are now here:
 *   * Research's "Re-fetch SERP (all pages on keyword)" — the expensive twin of
 *     "Extract facts", which the flat layout gave no way to relate to it. As a
 *     menu item under the same button, the escalation ladder reads itself:
 *     press Run for the cheap local pass, open the caret when that's not enough.
 *   * Outline's "Use angle's structure raw" checkbox — a run MODE, not a page
 *     setting, so it belongs on the button that consumes it, shown as a chip.
 *
 * Items come in two flavours, distinguished by whether `selected` is set:
 * mode items (a ✓ marks the one in force, picking one persists the choice) and
 * action items (picking one does the thing immediately).
 */
export type RunMenuItem = {
  key: string;
  label: string;
  blurb?: string;
  // Small pill next to the label — the honest cost/scope warning
  // ("slow · affects every page on this keyword").
  badge?: string;
  onSelect: () => void;
  disabled?: boolean;
  // Set (true/false) for mode items, left undefined for one-shot actions.
  selected?: boolean;
  tone?: 'default' | 'warn';
};

/** Matches the w-80 this menu has always been. */
const MENU_WIDTH = 320;

export default function PhaseRunSplitButton({
  label,
  onClick,
  disabled,
  title,
  themeClass,
  chip,
  icon,
  items,
  menuHeading,
  // Draws attention to the caret when the menu holds the action the user
  // probably needs right now (e.g. extraction failed on half the pages).
  caretAttention = false,
  caretTitle = 'More run options',
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  // The state-derived colour scheme (see runButtonTheme) — applied to both
  // halves so they read as one control.
  themeClass: string;
  chip?: ReactNode;
  icon?: ReactNode;
  items: RunMenuItem[];
  menuHeading: string;
  caretAttention?: boolean;
  caretTitle?: string;
}) {
  const [open, setOpen] = useState(false);
  const caretRef = useRef<HTMLButtonElement>(null);
  // Portalled, not an absolute child: the page-builder phase header is sticky
  // and this menu opened behind it. See common/anchoredPanel.ts.
  const pos = useAnchoredPanel(open, caretRef, MENU_WIDTH);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // No variants to offer → a plain button, so callers don't have to branch on
  // which phase they're rendering.
  if (items.length === 0) {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded disabled:opacity-50 whitespace-nowrap ${themeClass}`}
      >
        {icon}
        {label}
      </button>
    );
  }

  return (
    <div className="relative flex items-stretch">
      {/* Both halves wear the phase's theme; `border-r-0`/`border-l-0` keep the
          bordered ('ready') variant from doubling its stroke down the seam,
          which the divider span draws instead — one rule that lands on a white
          button and a saturated one alike. */}
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        className={`inline-flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 text-sm font-medium rounded-l border-r-0 disabled:opacity-50 whitespace-nowrap ${themeClass}`}
      >
        {icon}
        {label}
        {chip}
      </button>
      <span
        aria-hidden
        className="w-px self-stretch bg-black/15 dark:bg-white/25 shrink-0"
      />
      <button
        ref={caretRef}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={caretTitle}
        aria-label={caretTitle}
        aria-haspopup="true"
        aria-expanded={open}
        className={`icon-btn px-1.5 rounded-r border-l-0 disabled:opacity-50 ${themeClass} ${
 caretAttention ? 'ring-2 ring-amber-400 dark:ring-amber-600' : ''
 }`}
      >
        ▾
      </button>

      {open &&
        pos &&
        createPortal(
        <>
          {/* click-away */}
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div
            style={{
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: MENU_WIDTH,
              maxHeight: pos.maxHeight,
            }}
            className="fixed z-[101] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-1.5 text-left">
            <div className="px-2 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {menuHeading}
            </div>
            {items.map((item) => (
              <button
                key={item.key}
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className="w-full flex items-start gap-2.5 p-2 rounded-md text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-gray-900 dark:text-gray-100">
                    {item.label}
                    {item.badge && (
                      <span
                        className={`text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-px ${
                          item.tone === 'warn'
                            ? 'text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40'
                            : 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </span>
                  {item.blurb && (
                    <span className="block text-[11px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                      {item.blurb}
                    </span>
                  )}
                </span>
                {/* Mode items reserve the tick column so the labels of a
                    mode menu stay aligned; action menus don't get a column. */}
                {item.selected !== undefined && (
                  <span
                    className={`text-indigo-600 dark:text-indigo-400 font-bold text-xs mt-0.5 ${
                      item.selected ? '' : 'opacity-0'
                    }`}
                  >
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
